import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { fetchProjectActivity } from './projectActivity';
import {
  createComment,
  deleteComment,
  fetchCommentCounts,
  fetchCommentsByIds,
  fetchTaskComments,
  setCommentMentions,
  updateCommentBody,
} from './queries';
import type { CreateCommentInput } from './types';

/**
 * Server state for comments. A thread loads when a task panel opens (or the
 * project page renders its discussion), never with the task list; counts are
 * one project-wide read for the row indicator. A null taskId addresses the
 * project-level discussion thread.
 */

export const projectActivityKeys = {
  all: ['project-activity'] as const,
  forProject: (projectId: string) => [...projectActivityKeys.all, projectId] as const,
};

export const commentKeys = {
  all: ['comments'] as const,
  thread: (taskId: string | null, projectId: string) =>
    [...commentKeys.all, 'thread', taskId ?? `project:${projectId}`] as const,
  counts: (projectId: string) => [...commentKeys.all, 'counts', projectId] as const,
};

/** The signed-in user's id, or an empty string when access is not approved. */
function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useTaskComments(
  taskId: string | null,
  projectId: string,
  dealId: string | null = null,
  socialPostId: string | null = null,
  /** Only the newest N messages (Messages rooms). Left out, the whole thread. */
  limit?: number,
) {
  const { client } = useAuth();
  const baseKey =
    socialPostId !== null
      ? [...commentKeys.all, 'social', socialPostId]
      : dealId !== null
        ? [...commentKeys.all, 'deal', dealId]
        : commentKeys.thread(taskId, projectId);
  return useQuery({
    // The limit goes last, so invalidating commentKeys.thread(...) still
    // reaches every window of the same room.
    queryKey: limit === undefined ? baseKey : [...baseKey, limit],
    queryFn: () => fetchTaskComments(client, taskId, projectId, dealId, socialPostId, limit),
    // Asking for older messages keeps the rows on screen while they load, but
    // only inside the same room: another room's rows must never show.
    placeholderData: (previous, previousQuery) =>
      limit !== undefined && previousQuery?.queryKey[2] === baseKey[2] ? previous : undefined,
    // A deal or social thread has no project id, so requiring one here would
    // silently disable the query and show an empty thread forever.
    enabled: taskId !== '' && (projectId !== '' || dealId !== null || socialPostId !== null),
    retry: false,
  });
}

/**
 * The comments behind a room's pins that the loaded window does not hold, so
 * the Pinned strip still lists an old pin. Under the room's thread key, so
 * every refresh of the room reaches it. Nothing is read when ids is empty.
 */
export function usePinnedComments(roomId: string, ids: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...commentKeys.thread(null, roomId), 'pinned', ids],
    queryFn: () => fetchCommentsByIds(client, ids),
    enabled: roomId !== '' && ids.length > 0,
    retry: false,
  });
}

export function useCommentCounts(projectId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: commentKeys.counts(projectId ?? ''),
    queryFn: () => fetchCommentCounts(client, projectId as string),
    enabled: Boolean(projectId),
    retry: false,
  });
}

export function useCreateComment(
  taskId: string | null,
  projectId: string,
  dealId: string | null = null,
  socialPostId: string | null = null,
) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const authorId = useActorId();
  return useMutation({
    mutationFn: (input: Omit<CreateCommentInput, 'taskId' | 'projectId'>) =>
      createComment(client, { ...input, taskId, projectId, dealId, socialPostId, authorId }),
    onSuccess: () => {
      if (socialPostId !== null) {
        void queryClient.invalidateQueries({
          queryKey: [...commentKeys.all, 'social', socialPostId],
        });
        return;
      }
      if (dealId !== null) {
        // The board's Notes column reads the latest comment, so posting one
        // has to refresh the board as well as the thread.
        void queryClient.invalidateQueries({ queryKey: [...commentKeys.all, 'deal', dealId] });
        void queryClient.invalidateQueries({ queryKey: ['deals'] });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: commentKeys.thread(taskId, projectId) });
      void queryClient.invalidateQueries({ queryKey: commentKeys.counts(projectId) });
    },
  });
}

export function useUpdateComment(taskId: string | null, projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      commentId,
      body,
      mentionedUserIds,
      dealId,
      socialPostId,
    }: {
      commentId: string;
      body: string;
      /** Whoever the text mentions NOW, not who it mentioned when posted. */
      mentionedUserIds: string[];
      dealId?: string | null;
      socialPostId?: string | null;
    }) => {
      await updateCommentBody(client, commentId, body);
      // The body saved; the mentions are a second write and were missing
      // entirely until this. See setCommentMentions for what that cost.
      await setCommentMentions(client, {
        commentId,
        projectId,
        dealId: dealId ?? null,
        socialPostId: socialPostId ?? null,
        mentionedUserIds,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.thread(taskId, projectId) });
    },
  });
}

export function useDeleteComment(taskId: string | null, projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(client, commentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commentKeys.thread(taskId, projectId) });
      void queryClient.invalidateQueries({ queryKey: commentKeys.counts(projectId) });
    },
  });
}

/**
 * A project's own history, for the system lines in the Discussion thread.
 * Only project threads ask for it: a task drawer has its own activity list,
 * and asking here would show the wrong scope.
 */
export function useProjectActivity(projectId: string, enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: projectActivityKeys.forProject(projectId),
    queryFn: () => fetchProjectActivity(client, projectId),
    enabled,
    retry: false,
  });
}
